/**
 * V13.33 — Page publique d'un texte légal.
 *
 * - `status=published` requis (les brouillons renvoient 404 côté API)
 * - Redirection permanente client-side pour les slugs legacy (underscores)
 * - Message d'erreur clair si rubrique inexistante ou en préparation
 * - Affiche titre + version + date de dernière mise à jour
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle, ArrowLeft, Scale } from "lucide-react";
import { RgpdContactForm } from "@/components/RgpdContactForm";

// Alias legacy → clé canonique (redirect côté client)
const LEGACY_REDIRECTS = {
  droit_image: "images",
  "droit-image": "images",
  sources_externes: "sources-externes",
  mentions_legales: "mentions-legales",
  contact: "contact-droits",
  rgpd: "confidentialite",
};

// Référentiel des rubriques canoniques — doit rester aligné avec
// backend/routers/legal.py (REQUIRED_KEYS) et avec le footer / l'index.
const KNOWN_KEYS = new Set([
  "mentions-legales", "cgu", "confidentialite", "cookies", "contributeurs",
  "images", "mineurs", "ia", "contact-droits", "soutien",
]);
const DEFAULT_TITLES = {
  "mentions-legales": "Mentions légales",
  "cgu": "Conditions générales d'utilisation",
  "confidentialite": "Politique de confidentialité",
  "cookies": "Politique cookies et autres traceurs",
  "contributeurs": "Charte des contributeurs",
  "images": "Images et autorisations",
  "mineurs": "Protection des mineurs",
  "ia": "Intelligence artificielle",
  "contact-droits": "Contact et exercice des droits",
  "soutien": "Fonctions de soutien en préparation",
};

export default function LegalPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect legacy underscores → dashes en 301 côté client
  useEffect(() => {
    if (key && LEGACY_REDIRECTS[key]) {
      navigate(`/legal/${LEGACY_REDIRECTS[key]}`, { replace: true });
    }
  }, [key, navigate]);

  useEffect(() => {
    if (!key || LEGACY_REDIRECTS[key]) return;
    setLoading(true);
    setError(null);
    api
      .get(`/legal/${encodeURIComponent(key)}`)
      .then((r) => setItem(r.data))
      .catch((e) => {
        setItem(null);
        setError(e?.response?.status || "unknown");
      })
      .finally(() => setLoading(false));
  }, [key]);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-10" data-testid="legal-page">
      <Link
        to="/legal"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        data-testid="legal-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Retour à l'espace juridique
      </Link>

      {loading ? (
        <Card className="p-6">
          <Skeleton className="h-8 w-3/4 mb-3" />
          <Skeleton className="h-3 w-1/3 mb-6" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-2/3" />
        </Card>
      ) : error ? (
        <>
          {/* Si la clé fait partie du référentiel canonique, on affiche un
              placeholder « En préparation » cohérent plutôt qu'une erreur. */}
          {KNOWN_KEYS.has(key) ? (
            <Card className="p-6 border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon)/0.08)]" data-testid="legal-in-preparation">
              <div className="flex items-start gap-3">
                <Scale className="h-5 w-5 text-[hsl(var(--aito-lagoon))] shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h1 className="font-display text-xl font-semibold">
                    {DEFAULT_TITLES[key] || "Rubrique juridique"}
                  </h1>
                  <p className="text-xs text-muted-foreground font-mono mt-1">
                    identifiant : {key} · <span className="text-[hsl(var(--aito-lagoon))]">en préparation</span>
                  </p>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                    Cette rubrique est <strong>en cours de rédaction</strong> et sera publiée prochainement.
                    En attendant, tu peux consulter les autres documents juridiques déjà disponibles ou nous
                    écrire à <a href="mailto:Anna.yyc@gmail.com" className="underline">Anna.yyc@gmail.com</a> pour toute question.
                  </p>
                  <div className="mt-4">
                    <Link to="/legal">
                      <Button size="sm" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
                        <Scale className="h-4 w-4 mr-1.5" /> Voir toutes les rubriques
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-6 border-amber-300 bg-amber-50/50 dark:bg-amber-950/20" data-testid="legal-not-found">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h1 className="font-display text-xl font-semibold">Page juridique introuvable</h1>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    {error === 404
                      ? "Cette rubrique n'existe pas dans le référentiel actuel."
                      : "Impossible de charger cette page juridique pour le moment."}
                  </p>
                  <div className="mt-4">
                    <Link to="/legal">
                      <Button size="sm" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
                        <Scale className="h-4 w-4 mr-1.5" /> Voir toutes les rubriques
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          )}
          {/* V13.33 — Fallback : le formulaire de droits reste toujours
              accessible sur /legal/contact-droits, même quand la page n'est
              pas encore publiée. C'est le canal RGPD principal. */}
          {key === "contact-droits" && (
            <div className="mt-6">
              <RgpdContactForm />
            </div>
          )}
        </>
      ) : item ? (
        <>
          <Card className="p-6 md:p-8" data-testid="legal-content">
          <h1 className="font-display text-3xl font-bold" data-testid="legal-title">{item.title}</h1>
          <div className="text-xs text-muted-foreground font-mono mt-1 flex items-center gap-3 flex-wrap" data-testid="legal-meta">
            <span>identifiant : {item.key}</span>
            <span aria-hidden="true">·</span>
            <span>version {item.version}</span>
            {item.updated_at && (
              <>
                <span aria-hidden="true">·</span>
                <span>maj {new Date(item.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</span>
              </>
            )}
          </div>
          <div
            className="mt-6 text-[15px] leading-relaxed whitespace-pre-line prose prose-sm max-w-none dark:prose-invert"
            data-testid="legal-body"
          >
            {item.content}
          </div>
        </Card>
        {/* V13.33 — Formulaire d'exercice des droits injecté sous la page
            /legal/contact-droits une fois qu'elle est publiée. */}
        {key === "contact-droits" && (
          <div className="mt-6">
            <RgpdContactForm />
          </div>
        )}
        </>
      ) : null}
    </div>
  );
}
